import { useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { WORKSPACE_ROLES_LABELS } from "@common/constants";
import { OrganizationMemberWorkspaceAssignment } from "@common/dto/organization/organization-member.dto";
import { useWorkspacesList } from "@api/workspace.api";
import { EMPTY_CELL_VALUE } from "@components/organizationView/clients/views/cells/lib";
import {
    WorkspaceAvatarGroup,
    type WorkspaceAvatarGroupItem,
} from "@components/avatar/WorkspaceAvatarGroup";
import { TeamMemberRow } from "../teamViewConfig";

/** The member's clients as a stack, each with its role in the tooltip. */
const AssignedClients = ({
    workspaceAssignments,
}: {
    workspaceAssignments: OrganizationMemberWorkspaceAssignment[];
}) => {
    const workspacesListQuery = useWorkspacesList();
    const workspaces = useMemo<WorkspaceAvatarGroupItem[]>(() => {
        const workspacesById = new Map(
            workspacesListQuery.data?.map((workspace) => [
                workspace.id,
                workspace,
            ]) ?? [],
        );
        return workspaceAssignments.flatMap((assignment) => {
            const workspace = workspacesById.get(assignment.workspaceId);
            return workspace === undefined
                ? []
                : [
                      {
                          workspace,
                          tooltip: WORKSPACE_ROLES_LABELS[assignment.role],
                      },
                  ];
        });
    }, [workspaceAssignments, workspacesListQuery.data]);

    return <WorkspaceAvatarGroup workspaces={workspaces} max={3} size="xs" />;
};

export const ClientsCell = ({
    row,
}: CellContext<ViewTableFeatures, TeamMemberRow, any>) => {
    const member = row.original;

    if (
        member.type === "member" &&
        (member.role === OrganizationUserRole.OWNER ||
            member.role === OrganizationUserRole.ADMIN)
    ) {
        return <>Full access</>;
    }

    const workspaceAssignments =
        member.type === "member"
            ? member.workspaceAssignments
            : member.inviteAssignments;

    if (workspaceAssignments.length === 0) {
        return <span>{EMPTY_CELL_VALUE}</span>;
    }

    return <AssignedClients workspaceAssignments={workspaceAssignments} />;
};
